import { useEffect, useMemo, useRef, useState } from "react";
import { useCachedState } from "@/hooks/use-cached-state";
import { createFileRoute, Link } from "@tanstack/react-router";
import { TableSkeleton, CardSkeleton } from "@/components/loading-skeletons";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "@/hooks/use-user-role";
import { PageHeader } from "@/components/page-header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Upload, CheckCircle2, XCircle, RotateCcw, ShieldAlert, Smartphone, Copy } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app-updates")({
  head: () => ({ meta: [{ title: "OTA Update Aplikasi — MI Al-Munajah Admin" }] }),
  component: AppUpdatesPage,
});

type AppUpdate = {
  id: string;
  version_name: string;
  version_code: number;
  min_supported_version: number;
  release_notes: string | null;
  whats_new: string | null;
  apk_url: string;
  apk_size: number;
  apk_sha256: string;
  force_update: boolean;
  published: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
};

const emptyForm = {
  version_name: "",
  version_code: 0,
  min_supported_version: 1,
  release_notes: "",
  whats_new: "",
  force_update: false,
};

function formatBytes(b: number) {
  if (!b) return "0 B";
  const k = 1024;
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(b) / Math.log(k));
  return `${(b / Math.pow(k, i)).toFixed(2)} ${units[i]}`;
}

async function sha256Hex(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

async function writeAudit(appUpdateId: string | null, action: string, details: Record<string, unknown> = {}) {
  const { data: u } = await supabase.auth.getUser();
  if (!u.user) return;
  await supabase.from("app_updates_audit").insert({
    app_update_id: appUpdateId,
    action,
    actor: u.user.id,
    details: details as never,
  });
}

function AppUpdatesPage() {
  const { role, loading: roleLoading } = useUserRole();
  const [rows, setRows] = useCachedState<AppUpdate[]>("app-updates:rows", []);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState<AppUpdate | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [apkFile, setApkFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [delId, setDelId] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const isSuper = role === "superadmin";

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("app_updates")
      .select("*")
      .order("version_code", { ascending: false });
    if (error) toast.error(error.message);
    setRows((data ?? []) as AppUpdate[]);
    setLoading(false);
  };

  useEffect(() => {
    if (isSuper) load();
  }, [isSuper]);

  const publishedLatest = useMemo(() => rows.find((r) => r.published) ?? null, [rows]);

  if (roleLoading) {
    return <div className="p-6"><CardSkeleton lines={5} /></div>;
  }

  if (!isSuper) {
    return (
      <div>
        <PageHeader title="OTA Update Aplikasi" description="Kelola rilis APK aplikasi Android." />
        <Card className="p-8 rounded-2xl">
          <div className="flex flex-col items-center text-center gap-3">
            <div className="w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center">
              <ShieldAlert className="w-7 h-7 text-destructive" />
            </div>
            <div className="text-lg font-semibold">Akses ditolak</div>
            <div className="text-sm text-muted-foreground max-w-md">
              Halaman OTA Update Aplikasi hanya dapat diakses oleh Super Admin.
            </div>
            <Button asChild variant="outline">
              <Link to="/dashboard">Kembali ke Dashboard</Link>
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  const resetUpload = () => {
    setForm(emptyForm);
    setApkFile(null);
    setUploadProgress("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const openUpload = () => {
    resetUpload();
    setUploadOpen(true);
  };

  const onFilePick = (f: File | null) => {
    if (!f) { setApkFile(null); return; }
    if (!f.name.toLowerCase().endsWith(".apk")) {
      toast.error("File harus berformat .apk");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setApkFile(f);
  };

  const doUpload = async () => {
    if (!apkFile) return toast.error("Pilih file APK terlebih dahulu");
    if (!form.version_name.trim()) return toast.error("Version Name wajib diisi");
    if (!form.version_code || form.version_code < 1) return toast.error("Version Code harus > 0");

    setBusy(true);
    try {
      // Reject duplicate version_code
      const { data: dup } = await supabase
        .from("app_updates")
        .select("id")
        .eq("version_code", form.version_code)
        .maybeSingle();
      if (dup) {
        toast.error(`Version Code ${form.version_code} sudah ada`);
        setBusy(false);
        return;
      }

      setUploadProgress("Menghitung SHA256…");
      const sha = await sha256Hex(apkFile);

      setUploadProgress("Mengunggah APK…");
      const path = `v${form.version_code}-${Date.now()}.apk`;
      const { error: upErr } = await supabase.storage
        .from("android-updates")
        .upload(path, apkFile, {
          cacheControl: "31536000",
          upsert: false,
          contentType: "application/vnd.android.package-archive",
        });
      if (upErr) throw upErr;

      const { data: pub } = supabase.storage.from("android-updates").getPublicUrl(path);
      const apk_url = pub.publicUrl;

      setUploadProgress("Menyimpan metadata…");
      const { data: u } = await supabase.auth.getUser();
      const { data: inserted, error: insErr } = await supabase
        .from("app_updates")
        .insert({
          version_name: form.version_name.trim(),
          version_code: form.version_code,
          min_supported_version: form.min_supported_version || 1,
          release_notes: form.release_notes.trim() || null,
          whats_new: form.whats_new.trim() || null,
          apk_url,
          apk_size: apkFile.size,
          apk_sha256: sha,
          force_update: form.force_update,
          published: false,
          created_by: u.user?.id ?? null,
        })
        .select("id")
        .single();
      if (insErr) throw insErr;

      await writeAudit(inserted.id, "upload", {
        version_name: form.version_name,
        version_code: form.version_code,
        size: apkFile.size,
        sha256: sha,
      });

      toast.success("APK berhasil diunggah");
      setUploadOpen(false);
      resetUpload();
      load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Gagal mengunggah";
      toast.error(msg);
    } finally {
      setBusy(false);
      setUploadProgress("");
    }
  };

  const openEdit = (r: AppUpdate) => {
    setEditing(r);
    setForm({
      version_name: r.version_name,
      version_code: r.version_code,
      min_supported_version: r.min_supported_version,
      release_notes: r.release_notes ?? "",
      whats_new: r.whats_new ?? "",
      force_update: r.force_update,
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusy(true);
    const { error } = await supabase
      .from("app_updates")
      .update({
        version_name: form.version_name.trim(),
        min_supported_version: form.min_supported_version || 1,
        release_notes: form.release_notes.trim() || null,
        whats_new: form.whats_new.trim() || null,
        force_update: form.force_update,
      })
      .eq("id", editing.id);
    setBusy(false);
    if (error) return toast.error(error.message);
    await writeAudit(editing.id, "edit", { fields: ["version_name", "min_supported_version", "release_notes", "force_update"] });
    toast.success("Perubahan disimpan");
    setEditOpen(false);
    setEditing(null);
    load();
  };

  const setPublished = async (r: AppUpdate, published: boolean) => {
    setBusy(true);
    const { error } = await supabase
      .from("app_updates")
      .update({ published })
      .eq("id", r.id);
    setBusy(false);
    if (error) return toast.error(error.message);
    await writeAudit(r.id, published ? "publish" : "unpublish", { version_code: r.version_code });
    toast.success(published ? `v${r.version_name} dipublikasikan` : `v${r.version_name} di-unpublish`);
    load();
  };

  const rollback = async (r: AppUpdate) => {
    // Unpublish all newer, publish this one.
    setBusy(true);
    const { error: e1 } = await supabase
      .from("app_updates")
      .update({ published: false })
      .gt("version_code", r.version_code);
    if (e1) { setBusy(false); return toast.error(e1.message); }
    const { error: e2 } = await supabase
      .from("app_updates")
      .update({ published: true })
      .eq("id", r.id);
    setBusy(false);
    if (e2) return toast.error(e2.message);
    await writeAudit(r.id, "rollback", { rolled_back_to_version_code: r.version_code });
    toast.success(`Rollback ke v${r.version_name} berhasil`);
    load();
  };

  const remove = async () => {
    if (!delId) return;
    const target = rows.find((r) => r.id === delId);
    if (!target) { setDelId(null); return; }
    setBusy(true);
    try {
      // Delete APK from storage first
      const url = target.apk_url;
      const m = url.match(/\/android-updates\/([^?]+)/);
      const path = m ? decodeURIComponent(m[1]) : null;
      if (path) {
        await supabase.storage.from("android-updates").remove([path]);
      }
      const { error } = await supabase.from("app_updates").delete().eq("id", delId);
      if (error) throw error;
      await writeAudit(null, "delete", { version_code: target.version_code, version_name: target.version_name });
      toast.success("Versi dihapus");
      setDelId(null);
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Gagal menghapus");
    } finally {
      setBusy(false);
    }
  };

  const copySha = (sha: string) => {
    navigator.clipboard?.writeText(sha).then(() => toast.success("SHA256 disalin"));
  };

  return (
    <div>
      <PageHeader
        title="OTA Update Aplikasi"
        description="Kelola rilis APK aplikasi Android Al-Munajah. Hanya Super Admin."
        actions={
          <Button onClick={openUpload}>
            <Plus className="w-4 h-4" /> Upload APK
          </Button>
        }
      />

      {publishedLatest && (
        <Card className="p-4 rounded-2xl mb-4 bg-success/5 border-success/30">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-success/15 flex items-center justify-center shrink-0">
              <Smartphone className="w-5 h-5 text-success" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Versi aktif untuk Android</div>
              <div className="font-semibold">v{publishedLatest.version_name} · code {publishedLatest.version_code}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {formatBytes(publishedLatest.apk_size)} · {publishedLatest.force_update ? "Force Update" : "Optional Update"}
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-4 rounded-2xl">
        <div className="rounded-xl border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Versi</TableHead>
                <TableHead className="text-right">Code</TableHead>
                <TableHead>Ukuran</TableHead>
                <TableHead>SHA256</TableHead>
                <TableHead>Tanggal</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Force</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableSkeleton columns={8} />
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="py-12 text-center text-muted-foreground">Belum ada versi. Klik "Upload APK".</TableCell></TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">
                    v{r.version_name}
                    {r.release_notes && (
                      <div className="text-xs text-muted-foreground line-clamp-2 max-w-xs">{r.release_notes}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{r.version_code}</TableCell>
                  <TableCell className="text-sm">{formatBytes(r.apk_size)}</TableCell>
                  <TableCell className="text-xs font-mono">
                    <button className="inline-flex items-center gap-1 hover:text-foreground text-muted-foreground" onClick={() => copySha(r.apk_sha256)} title={r.apk_sha256}>
                      {r.apk_sha256.slice(0, 10)}…
                      <Copy className="w-3 h-3" />
                    </button>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(r.created_at).toLocaleString("id-ID")}
                  </TableCell>
                  <TableCell>
                    {r.published
                      ? <Badge variant="outline" className="bg-success/15 text-success border-success/30">Published</Badge>
                      : <Badge variant="outline" className="bg-muted text-muted-foreground">Draft</Badge>}
                  </TableCell>
                  <TableCell>
                    {r.force_update
                      ? <Badge variant="outline" className="bg-destructive/15 text-destructive border-destructive/30">Force</Badge>
                      : <Badge variant="outline">Optional</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end flex-wrap">
                      {r.published ? (
                        <Button size="sm" variant="outline" onClick={() => setPublished(r, false)} disabled={busy}>
                          <XCircle className="w-4 h-4" /> Unpublish
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => setPublished(r, true)} disabled={busy}>
                          <CheckCircle2 className="w-4 h-4" /> Publish
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => rollback(r)} disabled={busy} title="Publish versi ini dan unpublish semua versi yang lebih baru">
                        <RotateCcw className="w-4 h-4" /> Rollback
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => openEdit(r)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => setDelId(r.id)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={(o) => { if (!busy) { setUploadOpen(o); if (!o) resetUpload(); } }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Upload APK Baru</DialogTitle>
            <DialogDescription>Unggah file APK aplikasi Android. SHA256 & ukuran dihitung otomatis.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>File APK <span className="text-destructive">*</span></Label>
              <Input ref={fileRef} type="file" accept=".apk,application/vnd.android.package-archive" onChange={(e) => onFilePick(e.target.files?.[0] ?? null)} />
              {apkFile && (
                <div className="text-xs text-muted-foreground mt-1">
                  {apkFile.name} · {formatBytes(apkFile.size)}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Version Name <span className="text-destructive">*</span></Label>
                <Input placeholder="1.0.3" value={form.version_name} onChange={(e) => setForm({ ...form, version_name: e.target.value })} />
              </div>
              <div>
                <Label>Version Code <span className="text-destructive">*</span></Label>
                <Input type="number" min={1} value={form.version_code || ""} onChange={(e) => setForm({ ...form, version_code: parseInt(e.target.value || "0", 10) })} />
              </div>
            </div>
            <div>
              <Label>Min Supported Version Code</Label>
              <Input type="number" min={1} value={form.min_supported_version} onChange={(e) => setForm({ ...form, min_supported_version: parseInt(e.target.value || "1", 10) })} />
              <div className="text-xs text-muted-foreground mt-1">
                Client dengan version_code di bawah nilai ini harus force-update.
              </div>
            </div>
            <div>
              <Label>Apa yang baru? <span className="text-xs text-muted-foreground font-normal">(ringkasan singkat untuk dialog update)</span></Label>
              <Textarea rows={2} placeholder="Perbaikan bug dan peningkatan performa untuk pengalaman yang lebih baik." value={form.whats_new} onChange={(e) => setForm({ ...form, whats_new: e.target.value })} />
              <div className="text-xs text-muted-foreground mt-1">Tampil pada kotak sorotan di dialog update Android.</div>
            </div>
            <div>
              <Label>Catatan Rilis</Label>
              <Textarea rows={4} placeholder="Detail lengkap perubahan…" value={form.release_notes} onChange={(e) => setForm({ ...form, release_notes: e.target.value })} />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div>
                <div className="text-sm font-medium">Force Update</div>
                <div className="text-xs text-muted-foreground">Pengguna wajib update, tidak bisa menutup dialog.</div>
              </div>
              <Switch checked={form.force_update} onCheckedChange={(v) => setForm({ ...form, force_update: v })} />
            </div>
            {uploadProgress && <div className="text-sm text-muted-foreground">{uploadProgress}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)} disabled={busy}>Batal</Button>
            <Button onClick={doUpload} disabled={busy || !apkFile}>
              <Upload className="w-4 h-4" /> {busy ? "Mengunggah…" : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={(o) => { if (!busy) { setEditOpen(o); if (!o) setEditing(null); } }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Edit Versi</DialogTitle>
            <DialogDescription>Version Code & file APK tidak dapat diubah. Untuk mengganti APK, upload versi baru.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Version Name</Label>
                <Input value={form.version_name} onChange={(e) => setForm({ ...form, version_name: e.target.value })} />
              </div>
              <div>
                <Label>Version Code</Label>
                <Input type="number" value={form.version_code} disabled />
              </div>
            </div>
            <div>
              <Label>Min Supported Version Code</Label>
              <Input type="number" min={1} value={form.min_supported_version} onChange={(e) => setForm({ ...form, min_supported_version: parseInt(e.target.value || "1", 10) })} />
            </div>
            <div>
              <Label>Apa yang baru?</Label>
              <Textarea rows={2} value={form.whats_new} onChange={(e) => setForm({ ...form, whats_new: e.target.value })} />
            </div>
            <div>
              <Label>Catatan Rilis</Label>
              <Textarea rows={4} value={form.release_notes} onChange={(e) => setForm({ ...form, release_notes: e.target.value })} />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="text-sm font-medium">Force Update</div>
              <Switch checked={form.force_update} onCheckedChange={(v) => setForm({ ...form, force_update: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} disabled={busy}>Batal</Button>
            <Button onClick={saveEdit} disabled={busy}>Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus versi ini?</AlertDialogTitle>
            <AlertDialogDescription>File APK di storage juga akan dihapus. Tindakan ini tidak dapat dibatalkan.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={remove} disabled={busy} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
